import {
  type ActionPreview,
  type ComplianceFix,
  type ComplianceFixId,
  type ComplianceScope,
} from '@bartizo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { createAction, previewAction } from '../api/actions.js';
import { needsReauth } from '../api/client.js';
import { useReauth } from '../auth/reauth-context.js';
import { SKIP_LABEL } from '../lib/actions.js';
import { fixChoices, targetOf, type FixChoice } from '../lib/apply-fixes.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';

/**
 * Applying the fixes Bartizo knows how to make (P11.19), from the Compliance page.
 *
 * Section 12's High row, so two things Medium does not ask for: only an admin sees
 * the button, and the admin's credentials are asked for again — before the dialog
 * opens, as removing a server does, rather than after the API refuses. The rest is
 * the ban dialog's shape: a list to choose from, then what the API says would
 * happen (which servers, which are skipped, what to know first), then the button.
 *
 * What is sent is a list of fix ids and a target, nothing more: the commands behind
 * an id are the helper's own, so there is no text here that a server could run.
 */

const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : 'Something went wrong; try again.';

export interface ApplyFixesDialogProps {
  choices: readonly FixChoice[];
  scope: ComplianceScope;
  scopeName: string;
  onClose: () => void;
}

export function ApplyFixesDialog({
  choices,
  scope,
  scopeName,
  onClose,
}: ApplyFixesDialogProps): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { ensureReauthenticated } = useReauth();
  const [chosen, setChosen] = useState<ReadonlySet<ComplianceFixId>>(
    () => new Set(choices.map((each) => each.id)),
  );
  const [preview, setPreview] = useState<{
    answer: ActionPreview;
    fixes: ComplianceFixId[];
  }>();

  const body = (fixes: ComplianceFixId[]) =>
    ({
      type: 'apply_compliance_fixes',
      target: targetOf(scope),
      args: { fixes },
    }) as const;

  const review = useMutation({
    mutationFn: async () => {
      // Kept in the list's order, which is also the order they run in.
      const fixes = choices.map((each) => each.id).filter((id) => chosen.has(id));
      return { fixes, answer: await previewAction(body(fixes)) };
    },
    onSuccess: setPreview,
  });

  const apply = useMutation({
    mutationFn: async (fixes: ComplianceFixId[]) => {
      try {
        return await createAction(body(fixes));
      } catch (error) {
        // The re-check from before the dialog went stale while it was open.
        if (needsReauth(error) && (await ensureReauthenticated())) {
          return createAction(body(fixes));
        }
        throw error;
      }
    },
    onSuccess: (detail) => {
      void queryClient.invalidateQueries({ queryKey: ['actions'] });
      void navigate(`/actions/${encodeURIComponent(detail.id)}`);
    },
  });

  if (preview !== undefined) {
    const { answer, fixes } = preview;
    const shown = answer.servers.slice(0, 10);
    return (
      <Dialog
        title={`Apply ${fixes.length === 1 ? '1 fix' : `${String(fixes.length)} fixes`}?`}
        description={`On ${scopeName}. What the API says would happen; nothing has been sent yet.`}
        onClose={onClose}
      >
        <div className="stack">
          {answer.servers.length === 0 ? (
            <Alert tone="warning" title="No server in that target can run this">
              Every one is skipped; the reasons are below.
            </Alert>
          ) : (
            <div>
              <p>
                Sent to{' '}
                {answer.servers.length === 1
                  ? '1 server'
                  : `${String(answer.servers.length)} servers`}
                , which run them one after the other:
              </p>
              <ul className="text-sm">
                {shown.map((each) => (
                  <li key={each.serverId}>
                    {each.name}
                    {each.serverStatus === 'active' ? '' : ` (${each.serverStatus})`}
                  </li>
                ))}
                {answer.servers.length > shown.length && (
                  <li className="muted">and {answer.servers.length - shown.length} more</li>
                )}
              </ul>
              <p className="muted text-sm">
                A server where a check already passes is not changed by its fix.
              </p>
            </div>
          )}

          {answer.skipped.length > 0 && (
            <div>
              <p>Skipped:</p>
              <ul className="text-sm">
                {answer.skipped.slice(0, 10).map((each) => (
                  <li key={each.serverId}>
                    {each.name}: {SKIP_LABEL[each.reason]}
                  </li>
                ))}
                {answer.skipped.length > 10 && (
                  <li className="muted">and {answer.skipped.length - 10} more</li>
                )}
              </ul>
            </div>
          )}

          {answer.warnings.length > 0 && (
            <Alert tone="warning" title="Before you go ahead">
              <ul>
                {answer.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </Alert>
          )}

          {apply.isError && <Alert tone="danger">{errorText(apply.error)}</Alert>}

          <div className="dialog-actions">
            <Button
              onClick={() => {
                setPreview(undefined);
                apply.reset();
              }}
            >
              Back
            </Button>
            <Button
              variant="danger"
              busy={apply.isPending}
              busyLabel="Sending…"
              disabled={answer.servers.length === 0}
              onClick={() => {
                apply.mutate(fixes);
              }}
            >
              Apply on{' '}
              {answer.servers.length === 1
                ? '1 server'
                : `${String(answer.servers.length)} servers`}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title="Apply fixes"
      description="Bartizo makes only these: small, safe to repeat, and each leaves the server reachable. The rest of the list needs a person."
      onClose={onClose}
    >
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (chosen.size > 0) review.mutate();
        }}
      >
        <fieldset className="stack">
          <legend className="sr-only">Fixes to apply</legend>
          {choices.map((each) => (
            <label key={each.id} className="row">
              <input
                type="checkbox"
                checked={chosen.has(each.id)}
                onChange={(event) => {
                  setChosen((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(each.id);
                    else next.delete(each.id);
                    return next;
                  });
                }}
              />
              <span>
                <strong>{each.title}</strong>
                <span className="cell-sub">{each.does}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {review.isError && <Alert tone="danger">{errorText(review.error)}</Alert>}
        <div className="dialog-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="primary"
            busy={review.isPending}
            disabled={chosen.size === 0}
          >
            Review
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** The button that opens the dialog, after the credential re-check; admins only. */
export function ApplyFixesButton({
  fixes,
  scope,
  scopeName,
}: {
  fixes: readonly ComplianceFix[];
  scope: ComplianceScope;
  scopeName: string;
}): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const [open, setOpen] = useState(false);
  const choices = fixChoices(fixes);
  if (choices.length === 0) return null;
  return (
    <>
      <Button
        variant="primary"
        title="Bartizo makes these fixes on the servers, through the root helper. You are asked for your password first."
        onClick={() => {
          void ensureReauthenticated().then((ok) => {
            if (ok) setOpen(true);
          });
        }}
      >
        Apply {choices.length === 1 ? '1 fix' : `${String(choices.length)} fixes`}
      </Button>
      {open && (
        <ApplyFixesDialog
          choices={choices}
          scope={scope}
          scopeName={scopeName}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
}
